<script>
    import { createEventDispatcher } from 'svelte';
	import { navigate } from 'svelte-routing';

    import { hydrateInmate, getInmateSearchModel, getInmateSurveyModel, getInmateContactModel, getInmateDetailsTooltip,
        getTemporaryDischargeTooltip, getCircumstanceDateTooltip, getArrivalTooltip, ModalTypeEnum } from '../../inmate.js'
    import { getTriggerDatesTooltip, isDateInTriggerDates } from '../../trigger-date.js';
    import { getSurveyScheduleTooltip, getSurveyToastProperties } from "../../survey.js";
    import { getContactWarningTooltip } from '../../contact.js';
    import { isToday, getIsoDateStringAsDate } from '../../date-time.js';
    import { createRequestParams, isMatching } from "../../string.js";
    import { sortByProperty } from '../../sort.js';
    import { referenceDataEthnicityList, referenceDataRegimeLevelList, referenceDataCellStatusList } from "../../reference-data.js";
    import { vptServer, token, inmateCount, filterShowTargetedInmatesOnly, getTooltipDisplayOptions } from '../../stores.js';
    import { config, countActiveIsolationElements } from '../../config.js';

    import AimToast from '../../component/AimToast.svelte';
    import Badge from '../../component/Badge.svelte';
    import Dropdown from '../../component/Dropdown.svelte';
    import FilterButton from '../../component/FilterButton.svelte';
    
    import AddContactModal from '../../modal/AddContactModal.svelte';
    import ContactModal from '../../modal/ContactModal.svelte';
    import SurveyModal from '../../modal/SurveyModal.svelte';
    
    import TableHead from '../../table/TableHead.svelte';
    import TableData from '../../table/TableData.svelte';
    import TableAction from '../../table/TableAction.svelte';
    import TargetedData from '../../table/TargetedData.svelte';
    import InmateData from '../../table/InmateData.svelte';
    import LocationData from '../../table/LocationData.svelte';

    export let requestProperties;
    
    const dispatch = createEventDispatcher();

    let inmates = [];
    let filteredInmates = [];
    let sites = [];
    let searchCompleted = false;
    let sortProperty = "rowOrdering";
    let reverseSorted = false;

    let toastProperties = null; 
    
    // Table filters and controls
    let filterShowRecentlyChangedOnly = false;
    let filterShowNoEngagementOnly = false;
    let filterShowNewInmatesOnly = false;
    let filterShowRemandedOnly = false;
    let filterShowNonDischargedOnly = false;
    let filterShowTrendIncreaseOnly = false;
    let filterShowTriggerDatesOnly = false;
    let filterShowOverdueSurveysOnly = false;
    let filterShowWarningContactsOnly = false;
    let filterSiteName = "";
    let filterFreeText = "";
    let controlShowHighRiskScoresOnly = false;
    let controlShowRecentChanges = false;

    // Modal related variables
    let selectedInmate = null;
    let selectedModalType = null;

    // Automatically update filtered inmates list when sort property or filters are changed.
    $: {
        inmates.sort(sortByProperty(sortProperty, reverseSorted));
 
        filteredInmates = inmates.filter(function(inmate) {
            if ($filterShowTargetedInmatesOnly && !inmate.indicatorTargeted) {
                return false; }
            if (filterShowNoEngagementOnly && (!inmate.lacksEngagement || inmate.indicatorEarlyDays)) {
                return false; }
            if (filterShowRecentlyChangedOnly && inmate.recentChangeCount == 0) {
                return false; }
            if (filterShowNewInmatesOnly && !inmate.indicatorNewInmate) {
                return false; }
            if (filterShowRemandedOnly && !inmate.indicatorRemand) {
                return false; }
            if (filterShowNonDischargedOnly && inmate.dateTemporaryDischarged) {
                return false; }
            if (filterShowTrendIncreaseOnly && inmate.indicatorTrend != 'U') {
                return false; }
            if (filterShowTriggerDatesOnly && !(inmate.triggerDateList && inmate.triggerDateList.length > 0)) {
                return false; }
            if (filterShowOverdueSurveysOnly && !inmate.surveyOverdue) {
                return false; }
            if (filterShowWarningContactsOnly && !inmate.contactWarning) { 
                return false; }
            if ($config.showSiteName && filterSiteName != "" && !(filterSiteName == inmate.siteName)) {
                return false; }
            if (filterFreeText != "" && !isMatching(getInmateSearchModel(inmate), filterFreeText.trim())) {
                return false; }
            return true;
        });
    }

    $: {
        if (requestProperties && requestProperties.requestDate) {
            searchCompleted = false;
            getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    $: keyworkerExists = inmates.filter(i => i.keyworkerName).length > 0;

    $: numberOfActiveElements = countActiveIsolationElements($config);

    async function getDailyReport(requestString) {
        await fetch(`${$vptServer}/establishment/isolation/list/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.forEach((i,index,arr) => {
                arr[index] = {...i,
                    ...hydrateInmate(i, i.indicatorTrend, $referenceDataEthnicityList, $referenceDataRegimeLevelList, $referenceDataCellStatusList, requestProperties.requestDate, $config.numberOfDaysConsideredNewCircumstance),
                    recentChangeCount: countRecentChanges(i),
                    lacksEngagement: checkEngagement(i)
                };
        });
            inmates = data;
        })
        .catch(error => {
            console.error("Error getting isolation list");
            console.error(error);
            return;
        });

        if ($config.showSiteName) {
            sites = [];
            filteredInmates.forEach((inmate) => {
                if (inmate.siteUrn && !(sites.some(e => e.label === inmate.siteName))) {
                    let site = {value: inmate.siteUrn, label: inmate.siteName}
                    sites = [...sites, site];
                }
            });
            sites = sites.sort(sortByProperty("label", false));
        }

        searchCompleted = true;
    }

    function sort(newSortProperty, reverse) {
        if (newSortProperty === sortProperty) {
            reverseSorted = !reverseSorted;
        }
        else {
            sortProperty = newSortProperty;
            reverseSorted = reverse ? true : false;
        }
    }

    function countRecentChanges(inmate) {
        let count = 0;
        if (inmate.indicatorLevelChangedToHighVisit) { count++ };
        if (inmate.indicatorLevelChangedToHighWork) { count++ };
        if (inmate.indicatorLevelChangedToHighPhone) { count++ };
        if (inmate.indicatorLevelChangedToHighMeal) { count++ };
        if (inmate.indicatorLevelChangedToHighCanteen) { count++ };
        if (inmate.indicatorLevelChangedToHighGym) { count++ };
        if (inmate.indicatorLevelChangedToHighActivity) { count++ };
        return count;
    }

    function checkEngagement(inmate) {
        let count = 0;

        // For all enabled elements check how many have no engagement data
        if ( !($config.isolationVisitWeighting === 0) && (inmate.levelVisit === 3.0)) { count ++ }
        if ( !($config.isolationWorkWeighting === 0) && (inmate.levelWork === 3.0)) { count ++ }
        if ( !($config.isolationPhoneWeighting === 0) && (inmate.levelPhone === 3.0)) { count ++ }
        if ( !($config.isolationMealWeighting === 0) && (inmate.levelMeal === 3.0)) { count ++ }
        if ( !($config.isolationCanteenWeighting === 0) && (inmate.levelCanteen === 3.0)) { count ++ }
        if ( !($config.isolationGymWeighting === 0) && (inmate.levelGym === 3.0)) { count ++ }
        if ( !($config.isolationPaWeighting === 0) && (inmate.levelActivity === 3.0)) { count ++ }

        return (count >= $config.isolationLackOfEngagementElementThreshold);
    }

    function openModal(inmate, modalType) {
        selectedModalType = modalType;
        if (modalType === ModalTypeEnum.SURVEY) {
            selectedInmate = getInmateSurveyModel(inmate);
        } else if (modalType === ModalTypeEnum.ADD_CONTACT) {
            selectedInmate = { inmateUrn: inmate.inmateUrn, inmateName: inmate.inmateName, unseenDays : inmate.contactTargetedUnseenDays };
        } else if (modalType === ModalTypeEnum.CONTACT) {
            selectedInmate = getInmateContactModel(inmate);
        }
    }

    function closeModal(event) {
        if (event.detail.scheduleType != null) {
            toastProperties = getSurveyToastProperties(event.detail.scheduleType, event.detail.surveyScheduleTotal, selectedInmate.inmateName, selectedInmate.inmateUrn);
        }

        selectedInmate = null;
        selectedModalType = null;
        
        if (event.detail.refresh) {
            if (requestProperties && requestProperties.requestDate) {
                searchCompleted = false;
                getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
            }
        }
    }
 </script>

<div class="container-fluid">
    {#if searchCompleted && inmates.length > 0}
        <div class="row table-filters">
            <div class="col-sm-12">
                <div class="form-inline" role="toolbar" aria-label="Table filters">
                    <div>
                        <FilterButton on:click={() => {filterShowTargetedInmatesOnly.set(!($filterShowTargetedInmatesOnly))}} id="filter-targeted"
                                isActive={$filterShowTargetedInmatesOnly} classes={"mr-1 btn-img"}
                                title="Show only Targeted {$config.inmateLabelName.toLowerCase()}s on the Isolation aspect">
                            <img src="/assets/filter/target{$filterShowTargetedInmatesOnly ? '' : '-inverse'}.svg" alt={"Show only Targeted"} />
                        </FilterButton>

                        <FilterButton on:click={() => {filterShowNoEngagementOnly = !filterShowNoEngagementOnly}} id="filter-no-engagement"
                                isActive={filterShowNoEngagementOnly} classes={"mr-3 btn-img"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s outside the Early Days period with low engagement levels">
                            <img src="/assets/filter/question-square{filterShowNoEngagementOnly ? '' : '-inverse'}.svg" alt={"Show only No Engagement"} />
                        </FilterButton>
                            
                        <FilterButton on:click={() => {filterShowTrendIncreaseOnly = !filterShowTrendIncreaseOnly}} id="filter-trend"
                            isActive={filterShowTrendIncreaseOnly} icon={"arrow-up-short"} iconSize={"lg"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s whose combined level of isolation has increased compared to {isToday(getIsoDateStringAsDate(requestProperties.requestDate)) ? 'yesterday' : 'the previous day'}" />

                        <FilterButton on:click={() => {filterShowRecentlyChangedOnly = !filterShowRecentlyChangedOnly}} id="filter-recent"
                            isActive={filterShowRecentlyChangedOnly} icon={"exclamation-diamond"} classes={"mr-3"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s where an Element has increased to High in the last {$config.isolationNumberOfDaysConsideredNewLevel} days" />

                        <FilterButton on:click={() => {filterShowTriggerDatesOnly = !filterShowTriggerDatesOnly}} id="filter-trigger-dates"
                            isActive={filterShowTriggerDatesOnly} icon={"pin-angle-fill"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s with recent or upcoming {$config.triggerDatesLabelName}" />
                        
                        {#if $config.numberOfDaysToShowTemporaryDischarge}
                            <FilterButton on:click={() => {filterShowNonDischargedOnly = !filterShowNonDischargedOnly}} id="filter-non-discharged"
                                isActive={filterShowNonDischargedOnly} icon={"building-fill"} classes={"mr-1"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s who are currently on the premises" />
                        {/if}

                        <FilterButton on:click={() => {filterShowRemandedOnly = !filterShowRemandedOnly}} id="filter-remand"
                            isActive={filterShowRemandedOnly} icon={"hourglass-split"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s on Remand" />

                        <FilterButton on:click={() => {filterShowNewInmatesOnly = !filterShowNewInmatesOnly}} id="filter-new-inmates"
                            isActive={filterShowNewInmatesOnly} icon={"calendar4-week"} classes={"mr-3"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s who arrived in the last {$config.numberOfDaysConsideredNewInmate} day{$config.numberOfDaysConsideredNewInmate == 1 ? '' : 's'}" />

                        {#if $config.contactTypeId}
                            <FilterButton on:click={() => {filterShowWarningContactsOnly = !filterShowWarningContactsOnly}} id="filter-warning-contacts"
                                isActive={filterShowWarningContactsOnly} icon={"sticky"} alertIcon={"exclamation-circle"} classes={$config.surveyUrn ? "mr-1" : "mr-3"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s where Contact is suggested" />    
                        {/if}

                        {#if $config.surveyUrn}
                            <FilterButton on:click={() => {filterShowOverdueSurveysOnly = !filterShowOverdueSurveysOnly}} id="filter-survey-overdue"
                                isActive={filterShowOverdueSurveysOnly} icon={"clipboard-data"} alertIcon={"clock"} classes={"mr-3"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s whose {$config.surveyLabelName} Survey is overdue" />
                        {/if}

                        <FilterButton on:click={()=> {controlShowHighRiskScoresOnly = !controlShowHighRiskScoresOnly}} id="control-badge-display"
                            isActive={controlShowHighRiskScoresOnly} icon={"exclamation-octagon"} classes={"mr-1"}
                            title={"Show only the High Level indicators"} />
                        
                        <FilterButton on:click={()=> {controlShowRecentChanges = !controlShowRecentChanges}} id="control-recent-changes"
                            isActive={controlShowRecentChanges} icon={"shuffle"} title={"Highlight recent circumstance changes"} />
                    </div>

                    <div>
                        {#if $config.showSiteName && sites.length > 1}
                            <Dropdown bind:value={filterSiteName} id={"sites"} options={sites} allLabel={"All Sites"} classes="mr-3"/>
                        {/if}

                        <label for="search" class="sr-only">Free Text Search</label>
                        <input type="text" class="form-control" id="search" bind:value={filterFreeText} placeholder="Search..." />
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <div class="row">
        <div class="col-sm-12">
            {#if searchCompleted}
                {#if filteredInmates.length > 0 && !($inmateCount.filtered > $inmateCount.unfiltered)}
                    <table class="table table-sm table-borderless table-hover table-card">
                        <thead>
                            <tr>
                                <TableHead on:click={() => sort("rowOrdering")} propertyName={"rowOrdering"} width={'xs'}
                                        tooltipId={"target-header"} tooltipText={`${$config.inmateLabelName} is on the Targeted list(s)`} 
                                        {sortProperty} {reverseSorted} invertArrow={true}>
                                    <img id="target-header" src="/assets/header/target.svg" alt="{$config.inmateLabelName} is on the Targeted list(s)" />
                                </TableHead>

                                <TableHead on:click={() => sort("trendSort")} propertyName={"trendSort"} icon={"graph-up"}
                                    tooltipText={"Daily Change"} tooltipId={"trend-header"} 
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("inmateName")} propertyName={"inmateName"} title={$config.inmateLabelName}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("triggerDateSort", true)} propertyName={"triggerDateSort"} icon={"pin-angle-fill"}
                                    tooltipText={`Recent / Upcoming ${$config.triggerDatesLabelName}`} tooltipId={"trigger-date-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("cellStatusSort")} propertyName={"cellStatusSort"} icon={"file-person"}
                                    tooltipText="Cell Status" tooltipId={"cell-status-header"}
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("wingCellSort")} propertyName={"wingCellSort"} title={"Cell"}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("daysSinceTemporaryDischarge", true)} propertyName={"daysSinceTemporaryDischarge"} 
                                    icon={"building-fill"} tooltipText={"Temporary Discharge"} tooltipId={"temp-discharge-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("recentChangeCount", true)} propertyName={"recentChangeCount"} icon={"exclamation-diamond"}
                                    tooltipText={`Level has increased to High in the last ${$config.isolationNumberOfDaysConsideredNewLevel} days`}
                                    tooltipId={"recent-change-header"} {sortProperty} {reverseSorted} />

                                {#if !($config.isolationVisitWeighting === 0)}
                                    <TableHead on:click={() => sort("levelVisit")} propertyName={"levelVisit"} 
                                        title={$config.isolationVisitLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationVisitDescription && !($config.isolationVisitLabel == $config.isolationVisitDescription)) ? $config.isolationVisitDescription : null}
                                        tooltipId={"visits-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}
                                 
                                {#if !($config.isolationWorkWeighting === 0)}
                                    <TableHead on:click={() => sort("levelWork")} propertyName={"levelWork"} 
                                        title={$config.isolationWorkLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationWorkDescription && !($config.isolationWorkLabel == $config.isolationWorkDescription)) ? $config.isolationWorkDescription : null}
                                        tooltipId={"work-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !($config.isolationPhoneWeighting === 0)}
                                    <TableHead on:click={() => sort("levelPhone")} propertyName={"levelPhone"} 
                                        title={$config.isolationPhoneLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationPhoneDescription && !($config.isolationPhoneLabel == $config.isolationPhoneDescription)) ? $config.isolationPhoneDescription : null}
                                        tooltipId={"phone-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !($config.isolationMealWeighting === 0)}
                                    <TableHead on:click={() => sort("levelMeal")} propertyName={"levelMeal"} 
                                        title={$config.isolationMealLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationMealDescription && !($config.isolationMealLabel == $config.isolationMealDescription)) ? $config.isolationMealDescription : null}
                                        tooltipId={"meals-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !($config.isolationCanteenWeighting === 0)}
                                    <TableHead on:click={() => sort("levelCanteen")} propertyName={"levelCanteen"} 
                                        title={$config.isolationCanteenLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationCanteenDescription && !($config.isolationCanteenLabel == $config.isolationCanteenDescription)) ? $config.isolationCanteenDescription : null}
                                        tooltipId={"canteen-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !($config.isolationGymWeighting === 0)}
                                    <TableHead on:click={() => sort("levelGym")} propertyName={"levelGym"} 
                                        title={$config.isolationGymLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationGymDescription && !($config.isolationGymLabel == $config.isolationGymDescription)) ? $config.isolationGymDescription : null}
                                        tooltipId={"gym-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                {#if !($config.isolationPaWeighting === 0)}
                                    <TableHead on:click={() => sort("levelActivity")} propertyName={"levelActivity"} 
                                        title={$config.isolationPaLabel} width={'md'} center={true}
                                        tooltipText={($config.isolationPaDescription && !($config.isolationPaLabel == $config.isolationPaDescription)) ? $config.isolationPaDescription : null}
                                        tooltipId={"activities-header"} {sortProperty} {reverseSorted} invertArrow={true} />
                                {/if}

                                <TableHead on:click={() => sort("regimeLevelDescription")} propertyName={"regimeLevelDescription"} 
                                    title={"Regime"} {sortProperty} {reverseSorted} classes={"table-pad-left"} />

                                <TableHead on:click={() => sort("cashBalance")} propertyName={"cashBalance"} title={"Balance"} 
                                    {sortProperty} {reverseSorted} classes="hidden-col-lv-3" />

                                <TableHead on:click={() => sort("indicatorRemand", true)} propertyName={"indicatorRemand"} icon={"hourglass-split"}
                                    tooltipText={`${$config.inmateLabelName} is on remand`} tooltipId={"remand-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("status")} propertyName={"status"} 
                                    title={"Status"} {sortProperty} {reverseSorted} classes="hidden-col-lv-2" />

                                <TableHead on:click={() => sort("daysSinceArrival")} propertyName={"daysSinceArrival"}
                                    title={"Arrived"} {sortProperty} {reverseSorted} classes="hidden-col-lv-3" />

                                {#if keyworkerExists}
                                    <TableHead on:click={() => sort("keyworkerName")} propertyName={"keyworkerName"} 
                                        title={$config.keyworkerLabelName} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if $config.showSiteName && sites.length > 1}
                                    <TableHead on:click={() => sort("siteName")} propertyName={"siteName"}
                                        title={"Site"} {sortProperty} {reverseSorted} classes="hidden-col-lv-1" />
                                {/if}

                                {#if $config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("contactSort", true)} propertyName={"contactSort"}
                                        icon="sticky" tooltipText="Contact" tooltipId={"contact-header"}
                                        {sortProperty} {reverseSorted} action={true} />
                                {/if}

                                {#if $config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("surveyScheduleNextSurveyDate")} propertyName={"surveyScheduleNextSurveyDate"}
                                        icon="clipboard-data" tooltipText="{$config.surveyLabelName} Survey Schedule" tooltipId={"survey-header"}
                                        {sortProperty} {reverseSorted} invertArrow={true} action={true} />
                                {/if}
                            </tr> 
                        </thead>

                        <tbody>
                            {#each filteredInmates as inmate (inmate.inmateUrn)}
                                <tr on:click={() => {navigate(`/history/${inmate.inmateUrn}`)}}>
                                
                                    <!-- Targeted Indicator -->                                
                                    <TargetedData inmateUrn={inmate.inmateUrn} targeted={inmate.indicatorTargeted} 
                                        targetedTotal={inmate.targetedTotal} inmateLabel={$config.inmateLabelName} />
                                    
                                    <!-- Trend Indicator -->
                                    <TableData display={inmate.indicatorTrend == 'U' || inmate.indicatorTrend == 'D'}
                                        icon={inmate.indicatorTrend == 'U' ? "arrow-up-short" : "arrow-down-short"} iconSize={'xl'}
                                        tooltipText={`${$config.inmateLabelName}'s combined level of isolation has ${inmate.indicatorTrend == 'U' ? 'increased' : 'decreased'} compared to ${isToday(getIsoDateStringAsDate(requestProperties.requestDate)) ? 'yesterday' : 'the previous day'}`}
                                        tooltipId={`trend-indicator-${inmate.inmateUrn}`} classes={inmate.indicatorTrend == 'U' ? "text-danger" : "text-success"}/>

                                    <!-- Inmate -->
                                    <InmateData inmateUrn={inmate.inmateUrn} name={inmate.inmateName} 
                                        tooltipText={getInmateDetailsTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "right" })} />
                                
                                    <!-- Trigger Date Indicator -->
                                    <TableData display={inmate.triggerDateList && inmate.triggerDateList.length > 0} icon={"pin-angle-fill"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ align: "left", maxWidth: 500 })}
                                        tooltipText={getTriggerDatesTooltip(inmate.triggerDateList)}
                                        classes={isDateInTriggerDates(requestProperties.requestDate, inmate.triggerDateList) ? "text-danger" : ""} />

                                    <!-- Cell Status -->
                                    {#if $config.showCellStatusIcon}
                                        <TableData display={inmate.vptCellStatusUrn > 0 && inmate.cellStatusIcon} center={true} 
                                                tooltipId={`cell-status-data-${inmate.inmateUrn}`} tooltipText={inmate.cellStatusDescription}>
                                            <img id="cell-status-data-{inmate.inmateUrn}" src="/assets/{inmate.cellStatusIcon}.svg" alt="{inmate.cellStatusDescription}" />
                                        </TableData>
                                    {/if}

                                    <!-- Cell Location -->
                                    <LocationData inmateUrn={inmate.inmateUrn} unit={inmate.unit} wing={inmate.wing} cell={inmate.cell} 
                                        cellStartDate={inmate.cellStartDate} cellFinishDate={inmate.cellFinishDate} 
                                        highlight={controlShowRecentChanges && inmate.isRecentCellChange} />

                                    <!-- Temporary Discharge Indicator -->
                                    <TableData display={inmate.daysSinceTemporaryDischarge > 0} icon={"building-fill-x"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ maxWidth: 500 })}
                                        tooltipText={getTemporaryDischargeTooltip(inmate.daysSinceTemporaryDischarge, inmate.dischargedTo)} />

                                    <!-- Display Isolation Badges unless the inmate is temporarily discharged -->
                                    {#if !$config.numberOfDaysToShowTemporaryDischarge && !inmate.daysSinceTemporaryDischarge || (inmate.daysSinceTemporaryDischarge < $config.numberOfDaysToShowTemporaryDischarge)}

                                        <!-- Recent Change Indicator -->
                                        <TableData display={inmate.recentChangeCount > 0} icon={"exclamation-diamond"}
                                            tooltipText={`${inmate.recentChangeCount} level${inmate.recentChangeCount > 1 ? 's have' : ' has'} increased to High in the last ${$config.isolationNumberOfDaysConsideredNewLevel} days`}
                                            tooltipId={`recent-change-indicator-${inmate.inmateUrn}`}  />

                                        <!-- Visits -->
                                        {#if !($config.isolationVisitWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-visit-{inmate.inmateUrn}" score={inmate.levelVisit} label={inmate.labelLastVisit} lastDate={inmate.dateLastVisit} 
                                                    changeDate={inmate.dateLevelLastChangedVisit} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighVisit} attendedLabel={"Visited"} />
                                            </TableData>
                                        {/if}
                                        
                                        <!-- Work -->
                                        {#if !($config.isolationWorkWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-work-{inmate.inmateUrn}" score={inmate.levelWork} label={inmate.labelLastWork} lastDate={inmate.dateLastWork} 
                                                    changeDate={inmate.dateLevelLastChangedWork} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighWork} />
                                            </TableData>
                                        {/if}
                                        
                                        <!-- Phone -->
                                        {#if !($config.isolationPhoneWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-phone-{inmate.inmateUrn}" score={inmate.levelPhone} label={inmate.labelLastPhone} lastDate={inmate.dateLastPhone}
                                                    changeDate={inmate.dateLevelLastChangedPhone} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighPhone} attendedLabel={"Top Up"} />
                                            </TableData>
                                        {/if}
                                        
                                        <!-- Meals -->
                                        {#if !($config.isolationMealWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-meal-{inmate.inmateUrn}" score={inmate.levelMeal} label={inmate.labelLastMeal} lastDate={inmate.dateLastMeal} 
                                                    changeDate={inmate.dateLevelLastChangedMeal} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighMeal} attendedLabel={"Selected"} />
                                            </TableData>
                                        {/if}

                                        <!-- Canteen -->
                                        {#if !($config.isolationCanteenWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-canteen-{inmate.inmateUrn}" score={inmate.levelCanteen} label={inmate.labelLastCanteen} lastDate={inmate.dateLastCanteen}
                                                    changeDate={inmate.dateLevelLastChangedCanteen} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighCanteen} attendedLabel={"Purchased"}
                                                    additionalTooltipText={inmate.levelCanteen < 3 ? `${$config.inmateLabelName} spent ${inmate.levelCanteen == 1 ? "significantly" : ""} less than their 7 day average` : null} />
                                            </TableData>
                                        {/if}
                                        
                                        <!-- Gym -->
                                        {#if !($config.isolationGymWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-gym-{inmate.inmateUrn}" score={inmate.levelGym} label={inmate.labelLastGym} lastDate={inmate.dateLastGym}
                                                    changeDate={inmate.dateLevelLastChangedGym} showBadge={!controlShowHighRiskScoresOnly}
                                                    recentChange={inmate.indicatorLevelChangedToHighGym} />
                                            </TableData>
                                        {/if}
                                        
                                        <!-- Other Activities -->
                                        {#if !($config.isolationPaWeighting === 0)}
                                            <TableData center={true}>
                                                <Badge id="level-activity-{inmate.inmateUrn}" score={inmate.levelActivity} label={inmate.labelLastActivity} lastDate={inmate.dateLastActivity}
                                                    changeDate={inmate.dateLevelLastChangedActivity} showBadge={!controlShowHighRiskScoresOnly} 
                                                    recentChange={inmate.indicatorLevelChangedToHighActivity} />
                                            </TableData>
                                        {/if}
                                    {:else}
                                        <td class="table-text-well" colspan="{numberOfActiveElements + 1}">
                                            <span class="table-text-center">
                                                {$config.inmateLabelName} has been temporarily discharged for {inmate.daysSinceTemporaryDischarge} days
                                            </span>
                                        </td>
                                    {/if}    

                                    <!-- Regime -->
                                    <TableData display={inmate.regimeLevelDescription} text={inmate.regimeLevelDescription} tooltipId={`regime-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.regimeStartDate, inmate.regimeFinishDate)} 
                                        highlight={controlShowRecentChanges && inmate.isRecentRegimeChange} classes="table-pad-left" />
                                
                                    <!-- Balance -->
                                    <TableData display={inmate.cashBalance != null} text={`£${inmate.cashBalance ? inmate.cashBalance.toFixed(2) : "0"}`} classes="hidden-col-lv-3" />

                                    <!-- Remand Indicator -->
                                    <TableData display={inmate.indicatorRemand} icon={"hourglass-split"} />

                                    <!-- Status -->
                                    <TableData display={inmate.status} text={inmate.status} tooltipId={`status-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.statusStartDate, inmate.statusFinishDate)}
                                        highlight={controlShowRecentChanges && inmate.isRecentStatusChange} classes={"hidden-col-lv-2"} />

                                    <!-- Arrived -->
                                    <TableData tooltipId={`arrival-data-${inmate.inmateUrn}`} classes={"hidden-col-lv-3"}
                                            tooltipText={getArrivalTooltip(inmate.dateArrived, inmate.indicatorNewInmate, inmate.indicatorEarlyDays)}>
                                        <span id="arrival-data-{inmate.inmateUrn}"
                                                class:badge={inmate.indicatorNewInmate || inmate.indicatorEarlyDays}
                                                class:badge-arrival={inmate.indicatorNewInmate || inmate.indicatorEarlyDays}
                                                class:badge-danger={inmate.indicatorNewInmate}
                                                class:badge-border={inmate.indicatorNewInmate}
                                                class:badge-primary={!inmate.indicatorNewInmate && inmate.indicatorEarlyDays}>
                                            {inmate.timeSinceArrival}
                                        </span>
                                    </TableData>

                                    <!-- Keyworker -->
                                    {#if keyworkerExists}
                                        <TableData display={inmate.keyworkerName} text={inmate.keyworkerName} tooltipId={`keyworker-data-${inmate.inmateUrn}`}
                                            tooltipText={getCircumstanceDateTooltip(inmate.keyworkerStartDate, inmate.keyworkerFinishDate)} 
                                            highlight={controlShowRecentChanges && inmate.isRecentKeyworkerChange} />
                                    {/if}

                                    <!-- Site -->
                                    {#if $config.showSiteName && sites.length > 1}
                                        <TableData display={inmate.siteName} text={inmate.siteName} classes="hidden-col-lv-1" />
                                    {/if}

                                    <!-- Contact -->
                                    <TableAction display={$config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"sticky"} displayFilled={inmate.contactId} alertIcon={inmate.contactOverdue ? "clock" : "exclamation-circle"} displayAlert={inmate.contactWarning} 
                                        tooltipText={getContactWarningTooltip(inmate.contactId, inmate.contactTargetedUnseenDays, inmate.contactActionOverdueDays, inmate.contactIndicatorTargetedTotalIncreased, inmate.contactIndicatorIsolationIncreased)}
                                        tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, inmate.contactId ? ModalTypeEnum.CONTACT : ModalTypeEnum.ADD_CONTACT)}} />

                                    <!-- Survey Schedule -->
                                    <TableAction display={$config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"clipboard-data"} displayFilled={inmate.surveyScheduleUrn} alertIcon={"clock"} displayAlert={inmate.surveyOverdue}
                                        tooltipText={getSurveyScheduleTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, ModalTypeEnum.SURVEY)}} />
                                </tr>
                            {/each}
                        </tbody>
                    </table>

                    {#if selectedInmate}
                        {#if selectedModalType === ModalTypeEnum.SURVEY}
                            <SurveyModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.ADD_CONTACT}
                            <AddContactModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.CONTACT}
                            <ContactModal {...selectedInmate} on:close={closeModal} />
                        {/if}
                    {/if}
                {:else}
                    {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                        <div class="alert alert-warning" role="alert">
                            Data is being regenerated. Please wait a few moments and
                            <button type="button" class="btn btn-link refresh" on:click={() => dispatch("refresh")}>
                                Refresh
                            </button>
                        </div>
                    {:else if $inmateCount.unfiltered > 0}
                        <div class="alert alert-warning" role="alert">No {$config.inmateLabelName.toLowerCase()}s are currently being displayed. Please check the selected filters.</div>
                    {:else}
                        <div class="alert alert-danger" role="alert">No Isolation records were found for the selected date. Please try selecting a different date.</div>
                    {/if}
                {/if}
            {:else}
                <div class="spinner-container">
                    <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
                    <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
                </div>
            {/if}
        </div>
    </div>
</div>

<AimToast {...toastProperties}></AimToast>